import React, { Component } from 'react';
import { getConfig } from 'configs/edition.config';
import { DESCRIPTION_MAX, reportCaughtError, sendErrorReport } from 'utils/frontendMonitoring';

/**
 * ChunkLoadError를 처리하는 Error Boundary
 * 코드 스플리팅된 청크 로딩 실패 시 자동으로 페이지를 새로고침합니다.
 */
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      // 오류 화면의 신고 폼 (웹 전용)
      reportText: '',
      reportStatus: 'idle', // idle | sending | sent | failed
      reportId: '',
      reportError: '',
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // ChunkLoadError 감지
    const isChunkLoadError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module');

    if (isChunkLoadError) {
      console.error('ChunkLoadError 감지 - 페이지를 새로고침합니다:', error);

      // 무한 새로고침 방지: sessionStorage에 타임스탬프 저장
      const lastReloadTime = sessionStorage.getItem('lastChunkErrorReload');
      const now = Date.now();
      const reloadCooldown = 5000; // 5초 내에는 재시도하지 않음

      if (!lastReloadTime || (now - parseInt(lastReloadTime, 10)) > reloadCooldown) {
        sessionStorage.setItem('lastChunkErrorReload', now.toString());
        // 캐시를 무시하고 새로고침
        window.location.reload(true);
      }
    }

    // 일반 에러 로깅
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // 웹: QPM 으로 보낸다. React 가 잡은 렌더 에러는 window.onerror 로 올라가지 않아
    // RUM SDK 의 전역 훅이 못 본다 — 설치형이 아래에서 로그 파일로 넘기는 것과 같은 이유다.
    try {
      reportCaughtError(error, { type: 'react', componentStack: errorInfo?.componentStack });
    } catch (_) { /* 수집 실패가 에러 화면을 덮어쓰면 안 된다 */ }

    // 설치형: 로그 파일에도 남긴다.
    //
    // React 가 잡아 준 에러는 window.onerror 로 올라가지 않는다 — preload 의 전역 훅이
    // 못 본다는 뜻이다. 그래서 화면이 하얘지는 가장 전형적인 사고가 정작 로그 파일에는
    // 한 줄도 남지 않았다. 「도움말 › 로그 보기」로 리포팅하려면 여기서 넘겨야 한다.
    try {
      window.electronAPI?.logError?.(
        `ErrorBoundary: ${error?.message || error}`,
        `${error?.stack || ''}\n--- 컴포넌트 스택 ---${errorInfo?.componentStack || ''}`
      );
    } catch (_) { /* 로깅 실패가 에러 화면을 덮어쓰면 안 된다 */ }
  }

  handleSendReport = async () => {
    this.setState({ reportStatus: 'sending', reportError: '' });
    try {
      const { reportId } = await sendErrorReport({
        beApi: process.env.REACT_APP_BE_API,
        description: this.state.reportText,
        envExtra: { deployment: 'cloud', edition: getConfig().edition, trigger: 'error-boundary' },
      });
      this.setState({ reportStatus: 'sent', reportId });
    } catch (e) {
      this.setState({ reportStatus: 'failed', reportError: e.message });
    }
  };

  /**
   * 오류 화면의 신고 폼. 앱 트리가 무너진 상태라 공용 UI 컴포넌트 대신 기본 요소만 쓴다.
   * 흰 화면이 되는 순간이 사용자가 가장 신고하고 싶은 순간이다.
   */
  renderReportSection() {
    if (getConfig().isStandalone) {
      return (
        <div style={{ marginTop: '16px', fontSize: '13px', color: '#666', textAlign: 'center', maxWidth: '500px' }}>
          새로고침 후 「도움말 › 로그 보기」에서 리포트를 복사해 보내 주시면 원인을 찾는 데 도움이 됩니다.
        </div>
      );
    }
    const { reportStatus, reportText, reportId, reportError } = this.state;
    if (reportStatus === 'sent') {
      return (
        <div style={{ marginTop: '24px', fontSize: '14px', color: '#15803d', textAlign: 'center' }}>
          오류 신고가 접수되었습니다. 접수번호: <strong>{reportId}</strong>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>문의하실 때 이 번호를 알려 주세요.</div>
        </div>
      );
    }
    const sending = reportStatus === 'sending';
    return (
      <div style={{ marginTop: '24px', width: '100%', maxWidth: '500px' }}>
        <textarea
          value={reportText}
          onChange={(e) => this.setState({ reportText: e.target.value })}
          maxLength={DESCRIPTION_MAX}
          rows={3}
          disabled={sending}
          placeholder="무엇을 하던 중이었는지 적어 주시면 원인을 찾는 데 도움이 됩니다. (선택)"
          style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
        />
        {reportStatus === 'failed' && (
          <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{reportError}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <button
            onClick={this.handleSendReport}
            disabled={sending}
            style={{ padding: '8px 20px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: '4px', fontSize: '14px', cursor: sending ? 'default' : 'pointer' }}
          >
            {sending ? '보내는 중…' : '오류 신고 보내기'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const isChunkLoadError =
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk') ||
        error?.message?.includes('Failed to fetch dynamically imported module');

      // ChunkLoadError인 경우 로딩 화면 표시 (새로고침 대기)
      if (isChunkLoadError) {
        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '20px',
              color: '#333'
            }}>
              페이지를 새로고침하는 중...
            </div>
            <div style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f3f3',
              borderTop: '5px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
            <button
              onClick={() => window.location.reload(true)}
              style={{
                marginTop: '20px',
                padding: '10px 24px',
                backgroundColor: '#3498db',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              수동으로 새로고침
            </button>
          </div>
        );
      }

      // 일반 에러인 경우 에러 화면 표시
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 9999
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '16px',
            color: '#dc2626'
          }}>
            오류가 발생했습니다
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '24px',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            {error?.message || '알 수 없는 오류가 발생했습니다.'}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 32px',
              backgroundColor: '#3498db',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            새로고침
          </button>
          {this.renderReportSection()}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
